import { Classes, Colors, Dialog, Spinner } from "@blueprintjs/core";
import classNames from "classnames";
import sortBy from "lodash.sortby";
import {
  BaseFilterCriteriaInfo,
  DataTable2,
  FlexFieldGroup,
  IDataTable2Props,
  IDataTableColumn,
  ISearchExpressionPanelProps,
  IUseGridStateOptions,
  SearchExpressionPanel,
  SearchQuery,
  showError,
  showSuccess,
  SortKeysObject,
  useGridState,
  useSearchExpressionApi,
} from "nsitools-react";
import React from "react";
import { useLocation } from "react-router";
import styled from "styled-components";

import { CanDoAction, Dropzone, DropzoneFile, ExportButton } from "../";
import { ActionCode, FileResponse, PaginatedSearchable } from "../../api";
import { useEventsContext } from "../../contexts";
import { useBrowserStorage, useCommonHooks } from "../../hooks";

import { nameof } from "../../utils";
import { downloadFileResponse } from "../../utils/downloadFileResponse";
import { formatDate } from "../../utils/formatDate";
import {
  DownloadButton,
  ImportButton,
  SaveButton,
} from "../applicationButtons";
import { CardFieldGroup, CardFieldGroupProps } from "../formGenerator";

const DialogBody = styled.div`
  padding: 0.5rem;
  display: flex;
  flex-direction: column;

  & > * + * {
    margin-top: 0.5rem;
  }
`;

const StyledSearchExpressionPanel = styled(SearchExpressionPanel)`
  &.${Classes.INPUT_GROUP} .${Classes.BUTTON} {
    background-color: #f6f7f9 !important;
  }
`;

export interface IAdvancedSearchTableProps<
  TSearch extends PaginatedSearchable,
  TResults
> extends Pick<
      IDataTable2Props<any>,
      "customizeRowStyle" | "onRowClick" | "renderSubComponent" | "keyFieldName"
    >,
    Pick<CardFieldGroupProps, "title" | "rightElement">,
    Pick<
      ISearchExpressionPanelProps<any>,
      | "overrideListValues"
      | "defaultSelectedCriteria"
      | "storageMode"
      | "advancedPanelOptions"
      | "advancedModeDisabled"
      | "classicPanelOptions"
    >,
    Pick<
      IUseGridStateOptions<any>,
      "enableFilter" | "enableMultiSort" | "enablePagination"
    > {
  pageSize?: number;
  availablePageSizes?: number[];
  searchFunction: (
    searchQuery: SearchQuery<TSearch>
  ) => TResults | Promise<TResults>;
  exportFunction?: (searchQuery: SearchQuery<TSearch>) => Promise<FileResponse>;
  importFunction?: ({ fileName, data }) => void;
  getTemplateFunction?: () => Promise<FileResponse>;
  canDoExport?: ActionCode[];
  getCriteriasFunction?: () => Promise<BaseFilterCriteriaInfo[]>;
  columns: Array<IDataTableColumn>;
  criteriasTlPrefix?: string;
  sortKeys?: SortKeysObject;
  children?: (searchFn: Function) => React.ReactElement;
  showColumnSelector?: boolean;
  headerWrap?: boolean;
  withCards?: boolean;
  focusOnFilter?: boolean;
  highlightFilter?: boolean;
  storageKeySuffix?: string;
  saveCriteriaFunction?: (sObj: any) => void;
  savedSearches?: any[];
  deleteSelectedSearch?: (name: string) => void;
  defaultSearchId?: number;
  renderCustomSearchComponents?: (
    searchExpressionApi: ReturnType<
      typeof useSearchExpressionApi<TSearch, TResults>
    >
  ) => React.ReactNode;
  onToggleSelectAll?: () => Set<any> | Promise<Set<any>>;
  enableSelection?: boolean;
  keyFieldAccessor?: (item: any) => any;
  onSelectedKeysChange?: (selectedKeys: any[]) => void;
}

const StyledDataTable2 = styled(DataTable2)`
  th {
    font-weight: 500 !important;
    color: ${Colors.DARK_GRAY5} !important;
    vertical-align: middle !important;
  }

  &.header-wrap th {
    white-space: break-spaces !important;
  }

  td {
    //font-weight: 500 !important;
    vertical-align: middle !important;
  }

  &
    table.${Classes.HTML_TABLE}.${Classes.HTML_TABLE_STRIPED}
    tbody
    tr:nth-child(odd) {
  }

  & table.${Classes.HTML_TABLE}.${Classes.INTERACTIVE} tbody tr:hover {
  }

  & .${Classes.BUTTON_GROUP} > .${Classes.BUTTON} > .${Classes.ICON} {
    color: ${Colors.DARK_GRAY5}!important;
  }
  & .${Classes.BUTTON_GROUP} > .${Classes.BUTTON} {
    color: ${Colors.DARK_GRAY5}!important;
  }
`;

export function AdvancedSearchTable<
  TSearch extends PaginatedSearchable,
  TResults
>({
  searchFunction,
  exportFunction,
  importFunction,
  getTemplateFunction,
  canDoExport,
  getCriteriasFunction,
  columns,
  criteriasTlPrefix,
  overrideListValues,
  sortKeys = {},
  rightElement,
  customizeRowStyle,
  title,
  onRowClick,
  enablePagination = true,
  enableFilter = true,
  enableMultiSort = false,
  pageSize = 15,
  availablePageSizes = [15, 25, 50],
  storageMode = "sessionStorage",
  storageKeySuffix,
  children,
  showColumnSelector = true,
  headerWrap = false,
  withCards = true,
  focusOnFilter = false,
  highlightFilter = false,
  advancedPanelOptions = {},
  classicPanelOptions = {},
  deleteSelectedSearch,
  savedSearches,
  saveCriteriaFunction,
  defaultSearchId,
  renderSubComponent,
  keyFieldName,
  renderCustomSearchComponents,
  onToggleSelectAll,
  enableSelection,
  keyFieldAccessor,
  onSelectedKeysChange = () => {},
}: Readonly<IAdvancedSearchTableProps<TSearch, TResults>>) {
  const { router, currentLanguage, t, tUnsafe, notifyGlobalError } =
    useCommonHooks();
  const location = useLocation();
  const [importDialogOpened, setImportDialogOpened] = React.useState<boolean>();
  const [uploadedFiles, setUploadedFiles] = React.useState<DropzoneFile[]>([]);
  const [isImporting, setIsImporting] = React.useState(false);
  const [isGettingImportTemplate, setIsGettingImportTemplate] =
    React.useState(false);

  const getTemplateComputedFunction = React.useCallback(async () => {
    setIsGettingImportTemplate(true);
    try {
      const res = await getTemplateFunction();
      await downloadFileResponse(res);
    } catch (e) {
      console.error(e);
      showError(t("Error"));
    } finally {
      setIsGettingImportTemplate(false);
    }
  }, [getTemplateFunction, t]);

  const importComputedFunction = React.useCallback(async () => {
    setIsImporting(true);
    try {
      setIsImporting(true);
      if (uploadedFiles.length > 0) {
        importFunction({
          data: uploadedFiles[0].file,
          fileName: uploadedFiles[0].name,
        });
      }
      setImportDialogOpened(false);
      showSuccess(t("ImportSuccess"));
    } catch (err) {
      notifyGlobalError(err, "", true);
    } finally {
      setIsImporting(false);
    }
  }, [importFunction, notifyGlobalError, t, uploadedFiles]);

  const getStorageMode = React.useCallback(() => {
    switch (storageMode) {
      case "localeStorage":
        return "localeStorage";
      case "sessionStorage":
        return "sessionStorage";
      default:
        return "none";
    }
  }, [storageMode]);

  const { data: savedFilterSearch, set: setSavedFilterSearch } =
    useBrowserStorage("grid_filter_" + location.pathname, getStorageMode());

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination,
    enableFilter,
    enableMultiSort,
    availablePageSizes,
    pageSize,
    sortKeys: sortKeys,
    globalFilter: savedFilterSearch,
    storageOptions: {
      storageKey:
        "datagrid_" +
        router.pathname +
        (storageKeySuffix ? `_${storageKeySuffix}` : ""),
      columnCustomizationStorageMode: "localeStorage",
      globalFilterStorageMode: "sessionStorage",
      pageIndexStorageMode: "sessionStorage",
      pageSizeStorageMode: "localeStorage",
      sortKeysStorageMode: "sessionStorage",
    },
    enableSelection,
    keyFieldAccessor,
  });
  const { totalCount, selectedKeys } = tableState;

  React.useEffect(() => {
    if (selectedKeys) {
      onSelectedKeysChange([...selectedKeys]);
    }
  }, [onSelectedKeysChange, selectedKeys]);

  const innerSearchFunction = React.useCallback(
    (searchQuery: SearchQuery<TSearch>) => {
      if (searchQuery) {
        setSavedFilterSearch(searchQuery.criteria?.filter);
      }
      return searchFunction(searchQuery);
    },
    [searchFunction, setSavedFilterSearch]
  );

  const searchExpressionApi = useSearchExpressionApi({
    searchFunction: innerSearchFunction,
    tableState,
    initialSearch: !getCriteriasFunction,
    trimSearchObjectStringValues: true,
  });

  const { search, loading, searchData } = searchExpressionApi;

  const buttonSearchMethod = React.useCallback(
    (nextSearch?: SearchQuery<TSearch>) => {
      tableState.goToPage(1);
      return search(nextSearch);
    },
    [search, tableState]
  );

  const OnRefreshSearchTablesEvent = React.useCallback(() => {
    search(searchData);
  }, [search, searchData]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RefreshSearchTables", OnRefreshSearchTablesEvent);
    return () =>
      unsubscribeEvent("RefreshSearchTables", OnRefreshSearchTablesEvent);
  }, [OnRefreshSearchTablesEvent, subscribeToEvent, unsubscribeEvent]);

  const finalSearchCriteriaFunction = React.useMemo(() => {
    if (getCriteriasFunction && currentLanguage) {
      return async () => {
        const criteriasFunction = await getCriteriasFunction();
        return sortBy(
          criteriasFunction,
          nameof<BaseFilterCriteriaInfo>("criteria")
        );
      };
    }
  }, [currentLanguage, getCriteriasFunction]);

  const [exporting, setExporting] = React.useState(false);
  const exportGridData = React.useCallback(async () => {
    setExporting(true);
    try {
      const file = await exportFunction(searchData);
      await downloadFileResponse(file);
    } catch (e) {
      console.error(e);
      showError(t("Error"));
    }
    setExporting(false);
  }, [searchData, exportFunction, t]);

  const constructedRightElement = React.useMemo(() => {
    return (
      <>
        {rightElement}
        {exportFunction && (
          <CanDoAction actions={canDoExport}>
            <ExportButton
              small={true}
              onClick={exportGridData}
              loading={exporting}
            />
          </CanDoAction>
        )}
        {importFunction && (
          <CanDoAction actions={canDoExport}>
            <ImportButton
              small={true}
              onClick={() => setImportDialogOpened(true)}
            />
          </CanDoAction>
        )}
      </>
    );
  }, [
    canDoExport,
    exportFunction,
    exportGridData,
    exporting,
    importFunction,
    rightElement,
  ]);

  const [defaultSearchDone, setDefaultSearchDone] = React.useState(false);
  const [initialSavedSearch, setInitialSavedSearch] = React.useState<any>();
  React.useEffect(() => {
    if (!defaultSearchId || defaultSearchDone || savedSearches?.length === 0)
      return;
    setDefaultSearchDone(true);
    const initialSearch = savedSearches.find(
      (c) => c.searchCriteriaId === defaultSearchId
    );
    if (initialSearch) {
      setInitialSavedSearch(initialSearch);
    }
  }, [defaultSearchDone, defaultSearchId, savedSearches, search]);

  const getDefaultCriterias = React.useMemo(() => {
    if (classicPanelOptions?.defaultCriterias) {
      return classicPanelOptions?.defaultCriterias;
    } else if (!initialSavedSearch || initialSavedSearch?.advanced) {
      return undefined;
    } else {
      return JSON.parse(initialSavedSearch?.value);
    }
  }, [classicPanelOptions?.defaultCriterias, initialSavedSearch]);

  const getDefaultSearchExpression = React.useMemo(
    () =>
      initialSavedSearch?.value
        ? JSON.parse(initialSavedSearch?.value)
        : undefined,
    [initialSavedSearch?.value]
  );

  return (
    <FlexFieldGroup gap="0.5rem">
      {getCriteriasFunction && (
        <CardFieldGroup title={t("TableCriterias")} withCard={withCards}>
          {defaultSearchId && !initialSavedSearch ? (
            <Spinner />
          ) : (
            <div>
              {renderCustomSearchComponents &&
                renderCustomSearchComponents(searchExpressionApi)}
              <StyledSearchExpressionPanel
                getCriteriasFunc={finalSearchCriteriaFunction}
                onSearch={buttonSearchMethod}
                translateFunc={tUnsafe}
                tlDataPrefix={criteriasTlPrefix}
                overrideListValues={overrideListValues}
                triggerInitialSearch={true}
                criteriaIntent="success"
                storageMode={storageMode}
                storageKey={"search_" + location.pathname}
                saveCriteriaFunction={saveCriteriaFunction}
                savedSearches={savedSearches}
                deleteSelectedSearch={deleteSelectedSearch}
                advancedPanelOptions={{
                  ...advancedPanelOptions,
                  defaultSearchExpression: getDefaultSearchExpression,
                  viewerProps: {
                    inlineCondition: true,
                    ...advancedPanelOptions.viewerProps,
                  },
                }}
                advancedModeDisabled={true}
                classicPanelOptions={{
                  ...classicPanelOptions,
                  defaultCriterias: getDefaultCriterias,
                }}
                initialAdvancedMode={initialSavedSearch?.advanced}
              />
            </div>
          )}
        </CardFieldGroup>
      )}
      <CardFieldGroup
        title={title || `${totalCount} ${t("TableResults")}`}
        rightElement={constructedRightElement}
        withCard={withCards}
      >
        <StyledDataTable2
          tableState={tableState}
          loading={loading}
          columns={columns}
          showColumnSelector={showColumnSelector}
          customizeRowStyle={customizeRowStyle}
          onRowClick={onRowClick}
          filterMode="OnEnter"
          translateFunc={tUnsafe}
          formatDate={formatDate}
          className={classNames({ "header-wrap": headerWrap })}
          focusOnFilter={focusOnFilter}
          highlightFilter={highlightFilter}
          renderSubComponent={renderSubComponent}
          keyFieldName={keyFieldName}
          onToggleSelectAll={onToggleSelectAll}
        />
      </CardFieldGroup>
      {children ? children(search) : null}

      <Dialog
        isOpen={importDialogOpened}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        title={t("Import")}
        icon={"import"}
        onClose={() => {
          setImportDialogOpened(false);
        }}
      >
        <DialogBody>
          <Dropzone onFilesAdded={(f) => setUploadedFiles(f)} />
          {getTemplateFunction ? (
            <DownloadButton
              text={t("GetTemplate")}
              onClick={getTemplateComputedFunction}
              loading={isGettingImportTemplate}
            />
          ) : null}
          <SaveButton
            onClick={importComputedFunction}
            loading={isImporting}
            minimal={false}
            disabled={uploadedFiles.length === 0}
          />
        </DialogBody>
      </Dialog>
    </FlexFieldGroup>
  );
}
